import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company_name: '',
    cvr_number: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn er påkrævet';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrævet';
    } else if (!/^[+0-9\s-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail er påkrævet';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Virksomhedsnavn er påkrævet';
    }
    if (!formData.cvr_number.trim()) {
      newErrors.cvr_number = 'CVR-nummer er påkrævet';
    } else if (!/^\d{8}$/.test(formData.cvr_number.trim())) {
      newErrors.cvr_number = 'CVR-nummer skal være præcis 8 tal';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Beskrivelse er påkrævet';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company_name: formData.company_name,
          cvr_number: formData.cvr_number,
          description: formData.description,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          company_name: '',
          cvr_number: '',
          description: '',
          website_url: '',
        });
      } else {
        const data = await response.json();
        setStatus('error');
        setErrorMessage(data.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
    }
  };

  return (
    <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200/60 shadow-sm">
      <h3 className="text-xl font-bold text-slate-900 mb-6 pb-3 border-b border-slate-100 flex items-center gap-2.5">
        <i className="fa-solid fa-paper-plane text-[#2a4692] text-lg" />
        Send os en besked
      </h3>

      {status === 'success' ? (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-6 rounded-xl text-center">
          <i className="fa-solid fa-circle-check text-3xl text-emerald-500 mb-3" />
          <h4 className="font-bold text-lg mb-1">Tak for din besked!</h4>
          <p className="text-sm">Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.</p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-4 text-sm font-semibold text-[#2a4692] hover:underline cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - absolutely hidden */}
          <div className="sr-only absolute" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are a human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div className="md:col-span-1">
              <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1">
                Navn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-white border rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2a4692]/20 focus:border-[#2a4692] transition-all ${
                  errors.name ? 'border-red-500' : 'border-slate-200'
                }`}
                placeholder="Dit fulde navn"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500 font-medium">{errors.name}</p>}
            </div>

            {/* Company Name */}
            <div className="md:col-span-1">
              <label htmlFor="company_name" className="block text-sm font-semibold text-slate-700 mb-1">
                Virksomhedsnavn <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-white border rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2a4692]/20 focus:border-[#2a4692] transition-all ${
                  errors.company_name ? 'border-red-500' : 'border-slate-200'
                }`}
                placeholder="F.eks. KnowHow GROUP"
              />
              {errors.company_name && <p className="mt-1 text-xs text-red-500 font-medium">{errors.company_name}</p>}
            </div>

            {/* CVR Number */}
            <div className="md:col-span-1">
              <label htmlFor="cvr_number" className="block text-sm font-semibold text-slate-700 mb-1">
                CVR-nummer <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="cvr_number"
                name="cvr_number"
                value={formData.cvr_number}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-white border rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2a4692]/20 focus:border-[#2a4692] transition-all ${
                  errors.cvr_number ? 'border-red-500' : 'border-slate-200'
                }`}
                placeholder="F.eks. 21810797"
              />
              {errors.cvr_number && <p className="mt-1 text-xs text-red-500 font-medium">{errors.cvr_number}</p>}
            </div>

            {/* Phone */}
            <div className="md:col-span-1">
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1">
                Telefonnummer <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-white border rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2a4692]/20 focus:border-[#2a4692] transition-all ${
                  errors.phone ? 'border-red-500' : 'border-slate-200'
                }`}
                placeholder="F.eks. 50 358 378"
              />
              {errors.phone && <p className="mt-1 text-xs text-red-500 font-medium">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1">
                E-mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-white border rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2a4692]/20 focus:border-[#2a4692] transition-all ${
                  errors.email ? 'border-red-500' : 'border-slate-200'
                }`}
                placeholder="F.eks. info@dansketelemasterinvest.dk"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500 font-medium">{errors.email}</p>}
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-semibold text-slate-700 mb-1">
                Beskrivelse <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                rows="4"
                value={formData.description}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 bg-white border rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#2a4692]/20 focus:border-[#2a4692] transition-all resize-none ${
                  errors.description ? 'border-red-500' : 'border-slate-200'
                }`}
                placeholder="Skriv din besked her..."
              />
              {errors.description && (
                <p className="mt-1 text-xs text-red-500 font-medium">{errors.description}</p>
              )}
            </div>
          </div>

          {status === 'error' && (
            <div className="bg-red-50 border border-red-100 text-red-800 p-3.5 rounded-lg text-sm">
              <i className="fa-solid fa-triangle-exclamation mr-2" />
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-[#2a4692] hover:bg-[#1d3166] text-white font-bold py-3.5 px-4 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer shadow-xs hover:shadow-md"
          >
            {status === 'submitting' ? (
              <>
                <i className="fa-solid fa-spinner animate-spin" />
                Sender...
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" />
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
